import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Button } from '@diwata/ironvale'
import { MissionsAPI, type MissionPriority, type MissionStep, type WorkflowRun } from '@/api/missions'
import { DiwaAPI, ApiError } from '@/api/client'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { DomainsAPI, domainSlug, type Domain } from '@/api/domains'
import { Markdown } from '@/lib/markdownView'
import type { FitBrief } from '@/api/missions'
import { useSelection } from '@/store/selection'
import { useUI } from '@/store/ui'
import { useEntityPeek } from '@/components/EntityPeek'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import { detectOutbound } from '@/lib/outboundAction'
import { recordCrossing } from '@/lib/egressLog'
import './missions-agents.css'

// One mission as its own page — deep-linkable and owner-scoped, mirroring /domains/:slug.
// The machine's work IS the page: a vertical step spine for orientation, the Familiar's
// draft as the reading surface, and the operator's judgment (edit, approve, reject-with-
// direction) as the largest affordance on screen. Fetched fresh via GET /missions/{id}.

const GLYPH: Record<string, string> = {
  done: '●',
  running: '◉',
  awaiting_input: '◉',
  awaiting_gate: '◇',
  failed: '✕',
  pending: '○',
}

// The machine's state, in plain words — the operator should never have to decode
// `awaiting_gate`. `tone` picks the Badge variant / dot color.
const STATE_WORDS: Record<string, { label: string; tone: 'info' | 'success' | 'error' }> = {
  pending: { label: 'not started', tone: 'info' },
  running: { label: 'Tala is working…', tone: 'info' },
  awaiting_input: { label: 'waiting on input', tone: 'info' },
  awaiting_gate: { label: 'waiting for your call', tone: 'info' },
  done: { label: 'done', tone: 'success' },
  failed: { label: 'hit a snag', tone: 'error' },
}

/** The verdict, once: won/lost teach the win rate; no-bid and done close the loop. */
function CloseWithOutcome({ missionId }: { missionId: string }) {
  const { diwaUrl } = useUI()
  const qc = useQueryClient()
  const [note, setNote] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle')
  const [open, setOpen] = useState(false)

  async function close(outcome: string) {
    setState('busy')
    try {
      const api = new DiwaAPI(diwaUrl)
      await api.post(`/missions/${missionId}/outcome`, { outcome, note: note.trim() })
      await qc.invalidateQueries({ queryKey: ['missions'] })
      await qc.invalidateQueries({ queryKey: ['mission', missionId] })
      await qc.invalidateQueries({ queryKey: ['outcome-stats'] })
      setOpen(false)
    } catch {
      setState('error')
      return
    }
    setState('idle')
  }

  if (!open) {
    return (
      <div style={{ marginBottom: 14 }}>
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>Close with outcome…</Button>
      </div>
    )
  }
  return (
    <div style={{ marginBottom: 14, display: 'grid', gap: 8 }}>
      <input className="dw-input" placeholder="Note (optional) — what decided it?"
             value={note} onChange={(e) => setNote(e.target.value)} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {(['won', 'lost', 'no_bid', 'done'] as const).map((o) => (
          <Button key={o} size="sm" variant={o === 'won' ? 'primary' : 'ghost'}
                  disabled={state === 'busy'} onClick={() => close(o)}>
            {o.replace('_', '-')}
          </Button>
        ))}
        <button type="button" className="dw-back-link" style={{ padding: 0 }} onClick={() => setOpen(false)}>cancel</button>
        {state === 'error' && <span className="dw-error">Couldn't close — try again.</span>}
      </div>
    </div>
  )
}

const PRIORITIES: MissionPriority[] = ['High', 'Medium', 'Low']

// A protocol is a mission's script (the named steps/gates a kind of work follows). The raw
// token ("warm-intro/v1") means nothing to the operator — this glosses each in plain words:
// how Tala actually runs it. Free-form missions carry protocol="" and show no pill at all.
const PROTOCOL_GLOSS: Record<string, string> = {
  'bid-no-bid/v1': 'weigh a bid vs no-bid, cited',
  'research/v1': 'research the target and brief you',
  'warm-intro/v1': 'find a warm path and draft the intro',
  'outreach/v1': 'draft outreach for your review',
  'enrich/v1': 'enrich the target from your sources',
  'weekly-digest/v1': 'compile a weekly digest',
  'rule-review/v1': 'draft and backtest rule edits',
  'source-draft/v1': 'draft a new source and dry-run it',
}

/** Plain-words gloss for a protocol token — the mapped phrase, else a humanized fallback
 *  (drop the /vN suffix, dashes → spaces) so an unmapped protocol still reads in words. */
function protocolGloss(protocol: string): string {
  const known = PROTOCOL_GLOSS[protocol]
  if (known) return known
  return protocol.replace(/\/v\d+$/i, '').replace(/[-_]+/g, ' ').trim()
}

const FIT_GLYPH: Record<string, string> = { have: '●', partial: '◐', gap: '✕', unknown: '○' }

/** The fit brief: your side vs theirs, cited — the opening argument of a bid/no-bid. */
function FitBriefCard({ brief }: { brief: FitBrief }) {
  return (
    <div className="dw-fit-brief" style={{ marginBottom: 14, padding: '10px 12px', border: '1px solid var(--ae-color-border, rgba(128,128,128,.25))', borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div className="dw-rail-section-title" style={{ margin: 0 }}>Fit brief</div>
        <span className="dw-chip">{brief.recommendation}</span>
      </div>
      <div style={{ display: 'grid', gap: 4, fontSize: 'var(--ae-font-size-xs)' }}>
        {brief.checks.map((c) => (
          <div key={c.key}>
            <span className={`dw-fit-${c.verdict}`} style={{ marginRight: 6 }}>{FIT_GLYPH[c.verdict] ?? '○'}</span>
            <strong>{c.label}</strong>
            <span className="dw-inbox-detail"> — {c.detail}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Spine({
  steps,
  cursor,
  viewing,
  onView,
}: {
  steps: MissionStep[]
  cursor: string | null
  viewing: string | null
  onView: (step: MissionStep) => void
}) {
  return (
    <div className="dw-spine">
      {steps.map((s) => {
        const viewable = Boolean(s.artifact)
        const current = s.id === cursor
        return (
          <button
            key={s.id}
            type="button"
            disabled={!viewable}
            className={[
              'dw-spine-step',
              `dw-spine-step--${s.status}`,
              current ? 'dw-spine-step--current' : '',
              viewing && s.artifact === viewing ? 'dw-spine-step--viewing' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => viewable && onView(s)}
            title={viewable ? `Read the ${s.id} artifact` : s.status}
          >
            <span className="dw-spine-glyph">{GLYPH[s.status] ?? '○'}</span>
            <span className="dw-spine-label">{s.id}</span>
            {typeof s.attempts === 'number' && s.attempts > 1 && (
              <span className="dw-spine-meta">×{s.attempts}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** Inline editor for title / goal / steps — the domain-rename parity for a mission. Steps
 *  keep their status/artifact; you rename, reorder, toggle the review gate, add or drop. */
function MissionEdit({
  initialTitle,
  initialGoal,
  initialSteps,
  initialPriority,
  initialDomain,
  domains,
  busy,
  error,
  onSave,
  onCancel,
}: {
  initialTitle: string
  initialGoal: string
  initialSteps: MissionStep[]
  initialPriority: string
  initialDomain: string
  /** The user's own domains — the domain picker offers these; a free slug is still allowed. */
  domains: Domain[]
  busy: boolean
  error: string | null
  onSave: (patch: {
    title: string
    goal: string
    steps: MissionStep[]
    priority: MissionPriority
    domain: string
  }) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initialTitle)
  const [goal, setGoal] = useState(initialGoal)
  const [steps, setSteps] = useState<MissionStep[]>(initialSteps)
  const initP = (PRIORITIES as string[]).includes(initialPriority)
    ? (initialPriority as MissionPriority)
    : 'Medium'
  const [priority, setPriority] = useState<MissionPriority>(initP)
  const [domain, setDomain] = useState(initialDomain)
  // The mission's current domain may be a slug the picker doesn't list (a domain the user has
  // since left, or a display-name form). Keep it selectable so an edit never silently re-homes.
  const domainKnown = domains.some((d) => d.slug === domain)

  function updateStep(i: number, patch: Partial<MissionStep>) {
    setSteps((cur) => cur.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  }
  function removeStep(i: number) {
    if (steps.length <= 2) return // a mission needs at least two steps
    setSteps((cur) => cur.filter((_, j) => j !== i))
  }
  function move(i: number, delta: number) {
    const to = i + delta
    if (to < 0 || to >= steps.length) return
    setSteps((cur) => {
      const next = [...cur]
      ;[next[i], next[to]] = [next[to], next[i]]
      return next
    })
  }
  function addStep() {
    setSteps((cur) => [...cur, { id: '', status: 'pending' }])
  }

  const ids = steps.map((s) => s.id.trim())
  const blankTitle = title.trim().length === 0
  const hasBlankStep = ids.some((id) => id === '')
  const hasDupes = new Set(ids).size !== ids.length
  const invalid = blankTitle || hasBlankStep || hasDupes

  return (
    <div className="dw-mission-edit">
      <div className="dw-rail-section-title">Edit mission</div>
      <label className="dw-mission-edit-field">
        <span>Title</span>
        <input
          className="dw-input"
          value={title}
          placeholder="What this mission is called"
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Mission title"
        />
        {blankTitle && <span className="dw-field-error">A mission needs a title.</span>}
      </label>
      <label className="dw-mission-edit-field">
        <span>Goal</span>
        <textarea
          className="dw-input dw-textarea"
          rows={2}
          value={goal}
          placeholder="What you're asking Tala to do"
          onChange={(e) => setGoal(e.target.value)}
          aria-label="Mission goal"
        />
      </label>

      <div className="dw-mission-edit-row">
        <label className="dw-mission-edit-field">
          <span>Priority</span>
          <select
            className="dw-input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as MissionPriority)}
            aria-label="Mission priority"
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <label className="dw-mission-edit-field">
          <span>Domain</span>
          {domains.length > 0 ? (
            <select
              className="dw-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              aria-label="Mission domain"
            >
              {/* the current value, kept selectable even when it isn't one of your domains */}
              {!domainKnown && <option value={domain}>{domain || '—'}</option>}
              {domains.map((d) => (
                <option key={d.slug} value={d.slug}>{d.name}</option>
              ))}
            </select>
          ) : (
            <input
              className="dw-input"
              value={domain}
              placeholder="domain slug"
              onChange={(e) => setDomain(e.target.value)}
              aria-label="Mission domain"
            />
          )}
        </label>
      </div>

      <div className="dw-mission-edit-field">
        <span>Steps</span>
        <div className="dw-proposal-steps">
          {steps.map((s, i) => (
            <div key={i} className="dw-proposal-step">
              <div className="dw-proposal-step-reorder">
                <button type="button" className="dw-proposal-step-move" aria-label={`Move step ${i + 1} up`}
                        disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button type="button" className="dw-proposal-step-move" aria-label={`Move step ${i + 1} down`}
                        disabled={i === steps.length - 1} onClick={() => move(i, 1)}>↓</button>
              </div>
              <input
                className="dw-input dw-proposal-step-input"
                value={s.id}
                aria-label={`Step ${i + 1} name`}
                onChange={(e) => updateStep(i, { id: e.target.value })}
              />
              <button
                type="button"
                className={`dw-chip dw-chip--gate dw-proposal-step-gate${s.gate === 'review' ? ' dw-proposal-step-gate--on' : ''}`}
                aria-pressed={s.gate === 'review'}
                title={s.gate === 'review' ? 'Remove the review gate' : 'Pause here for your review'}
                onClick={() => updateStep(i, { gate: s.gate === 'review' ? 'none' : 'review' })}
              >
                ◇ review
              </button>
              {steps.length > 2 && (
                <button type="button" className="dw-proposal-step-x" aria-label={`Remove step ${i + 1}`}
                        onClick={() => removeStep(i)}>×</button>
              )}
            </div>
          ))}
        </div>
        <button type="button" className="dw-action-btn" style={{ justifySelf: 'start' }} onClick={addStep}>
          + Add step
        </button>
        {hasBlankStep && <span className="dw-field-error">Every step needs a name.</span>}
        {hasDupes && !hasBlankStep && <span className="dw-field-error">Step names must be unique.</span>}
      </div>

      <div className="dw-reader-actions">
        <Button
          size="sm"
          variant="primary"
          disabled={busy || invalid}
          onClick={() =>
            onSave({
              title: title.trim(),
              goal,
              steps: steps.map((s) => ({ ...s, id: s.id.trim() })),
              priority,
              domain: domain.trim(),
            })
          }
        >
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
      {error && <div className="dw-error">{error}</div>}
    </div>
  )
}

export function MissionPage() {
  const { id = '' } = useParams()
  const { diwaUrl } = useUI()
  const api = useMemo(() => new MissionsAPI(diwaUrl), [diwaUrl])
  const graphApi = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])
  const domainsApi = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const navigate = useNavigate()
  const selectNode = useSelection((s) => s.select)
  const { openPeek } = useEntityPeek()

  const { data: mission, isPending, error: missionError } = useQuery({
    queryKey: ['mission', id],
    queryFn: () => api.get(id),
    enabled: Boolean(id),
    retry: false,
  })

  const { data: runsData } = useQuery({
    queryKey: ['mission-runs', id],
    queryFn: () => api.listRuns(id),
    enabled: Boolean(mission),
  })
  const run: WorkflowRun | null = runsData?.runs[0] ?? null

  const { data: detail } = useQuery({
    queryKey: ['run-detail', id, run?.run_id],
    queryFn: () => api.runDetail(id, run!.run_id),
    enabled: Boolean(mission && run),
  })

  // Same cache key the Constellation uses — citations resolve against the live graph.
  const { data: network } = useQuery({
    queryKey: ['graph-network', 'all'],
    queryFn: () => graphApi.network('all'),
  })

  // The user's own domains — the same ['domains'] cache the app shell already warms. Drives the
  // smart domain chip (link only when the mission's domain is one you actually hold) and the
  // edit form's domain picker.
  const { data: domainsData } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainsApi.list(),
  })
  const domains = useMemo(() => domainsData?.domains ?? [], [domainsData])
  const byName = useMemo(() => {
    const m = new Map<string, GraphNode>()
    for (const n of network?.nodes ?? []) m.set(n.name.toLowerCase(), n)
    return m
  }, [network])

  const [viewingPath, setViewingPath] = useState<string | null>(null)
  const [viewSource, setViewSource] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // `editing` = the mission-level edit (title/goal/steps, header affordance).
  // `editingArtifact` = the gate's in-place draft edit. Kept apart so one never eats the other.
  const [editing, setEditing] = useState(false)
  const [editingArtifact, setEditingArtifact] = useState(false)
  const [editBusy, setEditBusy] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [guidance, setGuidance] = useState('')
  const [revising, setRevising] = useState(false)
  const [instruction, setInstruction] = useState('')
  // The release receipt for an approved outbound send — a page-level confirmation that
  // outlives the gate (which closes the moment the run advances). Cleared on a fresh run.
  const [released, setReleased] = useState<{ channel: string; to: string } | null>(null)

  // the stage follows the run: newest artifact (or the gated one) is what you're judging
  const steps: MissionStep[] = run ? (run.steps as MissionStep[]) : mission?.steps ?? []
  const gated = run?.status === 'awaiting_gate' ? steps.find((s) => s.id === run.cursor) : null
  useEffect(() => {
    const latest = gated?.artifact ?? [...steps].reverse().find((s) => s.artifact)?.artifact
    setViewingPath(latest ?? null)
    setEditingArtifact(false)
    setRejecting(false)
    setViewSource(false)
  }, [run?.run_id, run?.status, run?.cursor])

  const artifact = viewingPath ? detail?.artifacts[viewingPath] ?? null : null
  const judgingTheGate = Boolean(gated && viewingPath && gated.artifact === viewingPath)

  // Is the gated step an OUTBOUND ACTION (a message drafted to leave your world)? If so, the gate
  // renders as an action release, not a generic draft approval.
  const outbound = useMemo(
    () =>
      judgingTheGate && artifact
        ? detectOutbound({
            protocol: run?.protocol ?? mission?.protocol ?? '',
            artifact,
            target: mission?.target,
          })
        : null,
    [judgingTheGate, artifact, run?.protocol, mission?.protocol, mission?.target],
  )

  // The release receipt persists across the approve (which closes the gate); clear it only when a
  // genuinely new run begins.
  useEffect(() => {
    setReleased(null)
  }, [run?.run_id])

  async function act(fn: () => Promise<unknown>) {
    if (!mission) return
    setBusy(true)
    setError(null)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['mission-runs', id] })
      await qc.invalidateQueries({ queryKey: ['run-detail', id] })
      await qc.invalidateQueries({ queryKey: ['mission', id] })
      await qc.invalidateQueries({ queryKey: ['missions'] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not land. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function saveEdit(patch: {
    title: string
    goal: string
    steps: MissionStep[]
    priority: MissionPriority
    domain: string
  }) {
    if (!mission) return
    setEditBusy(true)
    setEditError(null)
    const prev = qc.getQueryData(['mission', id])
    // Optimistic — the page reflects the edit before the round-trip resolves.
    qc.setQueryData(['mission', id], (m: typeof mission | undefined) => (m ? { ...m, ...patch } : m))
    try {
      const updated = await api.update(id, patch)
      qc.setQueryData(['mission', id], updated)
      await qc.invalidateQueries({ queryKey: ['missions'] })
      setEditing(false)
    } catch (err) {
      qc.setQueryData(['mission', id], prev) // roll back the optimistic write
      setEditError(err instanceof ApiError ? err.detail ?? "Couldn't save changes. Try again." : "Couldn't save changes. Try again.")
    } finally {
      setEditBusy(false)
    }
  }

  function pickNode(n: GraphNode) {
    selectNode({
      id: n.id,
      name: n.name,
      type: n.type,
      world: n.world,
      score: n.score,
      domain: n.domain,
      attributes: n.attributes ?? null,
    })
    navigate('/graph')
  }

  function openTarget() {
    if (!mission) return
    const node = byName.get(mission.target.toLowerCase())
    if (node) pickNode(node)
  }

  // A cited entity in an artifact opens the peek anchored to the citation — a quick look with an
  // explicit route into the Constellation. (The target chip / "lives in your constellation" link
  // stay direct actions via pickNode: they carry their own intent to navigate.)
  function peekNode(n: GraphNode, anchor: HTMLElement) {
    openPeek(n, anchor)
  }

  // Owner-scoped 404 (or any load failure) → a clean not-found, mirroring DomainPage.
  if (!mission) {
    if (isPending) return <div className="dw-empty">Finding the mission…</div>
    const notFound = missionError instanceof ApiError && missionError.status === 404
    return (
      <div className="dw-empty">
        {notFound ? 'No mission here — it was closed, deleted, or never yours.' : "Couldn't load this mission."}{' '}
        <Link className="dw-link" to="/missions">
          Back to your missions →
        </Link>
      </div>
    )
  }

  const runnable = !run || run.status === 'pending' || run.status === 'running'
  const terminal = run && (run.status === 'done' || run.status === 'failed')
  // A mission BELONGS TO a domain — but a template can seed a domain the user doesn't hold
  // (e.g. weekly-digest → "growth"). The chip links only when the slug resolves to an owned
  // domain; otherwise it's a plain label, never a dead link. Tags (elsewhere) never navigate.
  const ownedDomain = domains.find((d) => d.slug === domainSlug(mission.domain)) ?? null
  // A mission TARGETS an entity — resolve it against the live graph so the chip can open the
  // entity peek (a quick look, with a route into the constellation). Plain text when unresolved.
  const targetNode = mission.target.trim()
    ? byName.get(mission.target.trim().toLowerCase()) ?? null
    : null

  return (
    <div className="dw-stage">
      <div className="dw-mission-crumb">
        <Link className="dw-domain-more" to="/missions">← all missions</Link>
      </div>

      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            {mission.title} <HelpHint term="mission" /> <FlowDiagram />
          </div>
          <div className="dw-page-subtitle">{mission.goal}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {run && (
            <Badge variant={STATE_WORDS[run.status]?.tone ?? 'info'}>
              {STATE_WORDS[run.status]?.label ?? run.status.replace('_', ' ')}
            </Badge>
          )}
          {!editing && (
            <button className="dw-action-btn" onClick={() => { setEditing(true); setEditError(null) }}>Edit</button>
          )}
        </div>
      </div>

      {editing ? (
        <MissionEdit
          key={mission.id}
          initialTitle={mission.title}
          initialGoal={mission.goal}
          initialSteps={mission.steps}
          initialPriority={mission.priority}
          initialDomain={domainSlug(mission.domain)}
          domains={domains}
          busy={editBusy}
          error={editError}
          onSave={saveEdit}
          onCancel={() => { setEditing(false); setEditError(null) }}
        />
      ) : (
        <>
          <div className="dw-chip-row" style={{ marginBottom: 14 }}>
            {/* TARGET → the entity peek: a quick look at the star this mission is aimed at, with a
                route into the constellation. Plain label when the target isn't (yet) a star. */}
            {targetNode ? (
              <button
                type="button"
                className="dw-chip dw-chip--toggle dw-chip--on"
                onClick={(e) => peekNode(targetNode, e.currentTarget)}
                title={`Quick look at ${mission.target}`}
              >
                ✦ {mission.target}
              </button>
            ) : (
              <span className="dw-chip" title="The target isn't a star in your constellation yet">
                ✦ {mission.target}
              </span>
            )}
            {/* DOMAIN this mission belongs to — a real link only when you actually hold it. */}
            {ownedDomain ? (
              <Link
                className="dw-chip"
                to={`/domains/${ownedDomain.slug}`}
                title={`open the ${ownedDomain.name} domain`}
              >
                {ownedDomain.name}
              </Link>
            ) : (
              <span className="dw-chip" title="This mission's domain isn't one you hold">
                {mission.domain}
              </span>
            )}
            {/* PROTOCOL — hidden for free-form missions; glossed in plain words when set. */}
            {mission.protocol && (
              <span className="dw-chip">
                <HelpHint term="protocol">protocol</HelpHint> / {protocolGloss(mission.protocol)}
              </span>
            )}
            <span className="dw-chip">priority / {mission.priority}</span>
          </div>

          {mission.brief && <FitBriefCard brief={mission.brief} />}

          {!mission.outcome ? (
            <CloseWithOutcome key={mission.id} missionId={mission.id} />
          ) : (
            <div className="dw-inbox-detail" style={{ marginBottom: 14 }}>
              Closed — {mission.outcome.replace('_', '-')}
              {mission.outcomeAt && ` · ${new Date(mission.outcomeAt).toLocaleDateString()}`}
            </div>
          )}
        </>
      )}

      <div className="dw-stage-body">
        <Spine
          steps={steps}
          cursor={run?.cursor ?? null}
          viewing={viewingPath}
          onView={(s) => {
            setViewingPath(s.artifact ?? null)
            setEditingArtifact(false)
            setRejecting(false)
            setViewSource(false)
          }}
        />

        <div className="dw-reader">
          {artifact === null ? (
            <div className="dw-reader-empty">
              {run
                ? 'Tala has not produced this artifact yet.'
                : 'Run the mission — Tala’s artifacts land here for your judgment.'}
            </div>
          ) : editingArtifact ? (
            <>
              <textarea
                className="dw-input dw-reader-edit"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                aria-label="Edit the artifact"
              />
              <div className="dw-reader-actions">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy}
                  onClick={() =>
                    act(async () => {
                      await api.editArtifact(mission.id, run!.run_id, viewingPath!, editText)
                      setEditingArtifact(false)
                    })
                  }
                >
                  Save edit
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditingArtifact(false)}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="dw-reader-toolbar">
                <button
                  type="button"
                  className="dw-reader-source-toggle"
                  aria-pressed={viewSource}
                  onClick={() => setViewSource((v) => !v)}
                  title={viewSource ? 'Show the rendered artifact' : 'Show the raw Markdown source'}
                >
                  {viewSource ? 'rendered' : 'view source'}
                </button>
              </div>
              {viewSource ? (
                <pre className="dw-reader-source">{artifact}</pre>
              ) : (
                <div className="dw-reader-body">
                  <Markdown text={artifact} byName={byName} onPick={peekNode} />
                </div>
              )}
              {judgingTheGate && (
                <div className={`dw-gate${outbound ? ' dw-gate--action' : ''}`}>
                  <div className="dw-gate-head">
                    <span className="dw-gate-glyph" aria-hidden="true">{outbound ? '↗' : '◇'}</span>
                    <span className="dw-section-title dw-gate-title">
                      {outbound ? 'Release this send' : 'Your call'}
                    </span>
                  </div>
                  {outbound ? (
                    <>
                      <p className="dw-gate-sub">
                        Tala drafted an outbound {outbound.channel} to leave your world. You are the
                        release authority — <strong>nothing leaves your world without this gate.</strong>{' '}
                        Approving hands it to your browser extension to send; the crossing is logged as
                        evidence.
                      </p>
                      <div className="dw-action-envelope">
                        <span className="dw-lane-badge dw-lane-badge--outer">Outer</span>
                        <span className="dw-action-field">
                          <span className="dw-action-key">channel</span>
                          <span className="dw-action-val">{outbound.channel}</span>
                        </span>
                        <span className="dw-action-arrow" aria-hidden="true">→</span>
                        <span className="dw-action-field">
                          <span className="dw-action-key">to</span>
                          <span className="dw-action-val">{outbound.to}</span>
                        </span>
                        {outbound.subject && (
                          <span className="dw-action-field">
                            <span className="dw-action-key">subject</span>
                            <span className="dw-action-val">{outbound.subject}</span>
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="dw-gate-sub">
                      {revising
                        ? 'Tell Tala how to revise this draft — she rewrites it in place.'
                        : rejecting
                          ? 'Send it back with direction — Tala redoes the step from your note.'
                          : `Tala finished ${run!.cursor} and is waiting for your judgment. Approve to move on, or send it back.`}
                    </p>
                  )}

                  {!rejecting && !revising && (
                    <div className="dw-reader-actions dw-reader-actions--gate">
                      {outbound ? (
                        <div className="dw-action-release">
                          <Button
                            size="md"
                            variant="primary"
                            disabled={busy}
                            onClick={() =>
                              act(async () => {
                                recordCrossing({
                                  channel: outbound.channel,
                                  to: outbound.to,
                                  subject: outbound.subject,
                                  preview:
                                    outbound.body.slice(0, 140) + (outbound.body.length > 140 ? '…' : ''),
                                  missionId: mission.id,
                                  missionTitle: mission.title,
                                  step: run!.cursor,
                                })
                                await api.decideGate(mission.id, run!.run_id, 'approve')
                                setReleased({ channel: outbound.channel, to: outbound.to })
                              })
                            }
                          >
                            ↗ Approve &amp; queue send
                          </Button>
                          <span className="dw-action-hint">
                            sends through your browser extension — coming; queued + logged now
                          </span>
                        </div>
                      ) : (
                        <Button
                          size="md"
                          variant="primary"
                          disabled={busy}
                          onClick={() => act(() => api.decideGate(mission.id, run!.run_id, 'approve'))}
                        >
                          Approve
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          setEditText(artifact)
                          setEditingArtifact(true)
                        }}
                      >
                        Edit draft
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRevising(true)}>
                        ✦ Revise
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        title="Re-author this step from scratch — no direction given"
                        onClick={() =>
                          act(() => api.regenerateArtifact(mission.id, run!.run_id, viewingPath!))
                        }
                      >
                        ↻ Regenerate
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRejecting(true)}>
                        {outbound ? 'Reject — don’t send' : 'Reject & redo'}
                      </Button>
                    </div>
                  )}
                  {revising && (
                    <div className="dw-reject-form">
                      <textarea
                        className="dw-input dw-textarea"
                        rows={2}
                        placeholder="Tell Tala how to revise this draft — she rewrites in place."
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                      />
                      <div className="dw-reader-actions">
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={busy || !instruction.trim()}
                          onClick={() =>
                            act(async () => {
                              await api.reviseArtifact(mission.id, run!.run_id, viewingPath!, instruction)
                              setInstruction('')
                              setRevising(false)
                            })
                          }
                        >
                          {busy ? '✦ Revising…' : '✦ Revise draft'}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRevising(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  {rejecting && (
                    <div className="dw-reject-form">
                      <textarea
                        className="dw-input dw-textarea"
                        rows={2}
                        placeholder="Direction for the redo — e.g. shorter, lead with the podcast."
                        value={guidance}
                        onChange={(e) => setGuidance(e.target.value)}
                      />
                      <div className="dw-reader-actions">
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={busy}
                          onClick={() =>
                            act(async () => {
                              await api.decideGate(mission.id, run!.run_id, 'reject', guidance)
                              setGuidance('')
                              setRejecting(false)
                            })
                          }
                        >
                          Reject with direction
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRejecting(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {run?.status === 'done' && (
        <div className="dw-mission-lands">
          <span className="dw-mission-lands-glyph" aria-hidden="true">●</span>
          Mission complete. Every step Tala took is stamped in her{' '}
          <Link to="/agents" className="dw-link">ledger</Link>, and{' '}
          <button type="button" className="dw-inline-link" onClick={openTarget}>
            {mission.target} lives in your constellation →
          </button>
        </div>
      )}

      <div className="dw-stage-controls">
        {!run && (
          <Button size="sm" variant="primary" disabled={busy} onClick={() => act(() => api.startRun(mission.id))}>
            ▶ Run with Familiar
          </Button>
        )}
        {run && runnable && (
          <Button size="sm" variant="primary" disabled={busy} onClick={() => act(() => api.runStep(mission.id, run.run_id))}>
            {busy ? '✦ The familiar is working…' : `▶ Run step: ${run.cursor}`}
          </Button>
        )}
        {terminal && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => act(() => api.startRun(mission.id))}>
            ▶ Start fresh run
          </Button>
        )}
        {run?.status === 'awaiting_gate' && !judgingTheGate && (
          <span className="dw-chip dw-chip--gate">◇ waiting for your call on {run.cursor}</span>
        )}
        <span style={{ flex: 1 }} />
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            if (window.confirm(`Delete mission "${mission.title}"?`)) {
              act(async () => {
                await api.remove(mission.id)
                await qc.invalidateQueries({ queryKey: ['outcome-stats'] })
                navigate('/missions')
              })
            }
          }}
        >
          Delete mission
        </Button>
      </div>

      {released && (
        <div className="dw-notice dw-run-released">
          <span className="dw-run-released-glyph" aria-hidden="true">↗</span>
          Queued for your extension — <strong>{released.channel} → {released.to}</strong>. Nothing has
          been sent yet; the send runs in your browser extension (coming). The crossing is logged as
          evidence in your{' '}
          <Link to="/agents" className="dw-link">crossings log</Link>.
        </div>
      )}

      {error && <div className="dw-notice dw-run-error">{error}</div>}
    </div>
  )
}
